import React, { useState, useRef, useEffect, memo } from "react";
import { createPortal } from "react-dom";
import { User, LogOut, ChevronDown, Lock } from "lucide-react";
import { authService } from "../../services/api";

const Navbar = memo(
  function Navbar({ currentPage, darkMode, handleLogout, isEmployeeView }) {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);
    const [user, setUser] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({
      top: 0,
      right: 0,
    });
    const [companyData, setCompanyData] = useState(null);

    // Change password state
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [changePasswordData, setChangePasswordData] = useState({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    const [changePasswordLoading, setChangePasswordLoading] = useState(false);
    const [changePasswordMessage, setChangePasswordMessage] = useState("");

    useEffect(() => {
      const user = JSON.parse(localStorage.getItem("currentUser"));
      setUser(user);

      // Try to get company data from localStorage
      let companyData = null;
      try {
        const storedCompanyData = localStorage.getItem("companyData");
        if (storedCompanyData) {
          companyData = JSON.parse(storedCompanyData);
        }
      } catch (error) {
        console.error("Error parsing company data:", error);
      }

      setCompanyData(companyData);

      // Set browser tab title immediately
      if (companyData?.name) {
        document.title = companyData.name;
      }

      function handleClickOutside(event) {
        // Check if click is outside the dropdown button and the portal dropdown
        const isOutsideButton =
          dropdownRef.current && !dropdownRef.current.contains(event.target);
        const isOutsidePortal = !event.target.closest("[data-portal-dropdown]");

        if (isOutsideButton && isOutsidePortal) {
          setIsDropdownOpen(false);
        }
      }

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    // Listen for changes in localStorage to update company data and tab title
    useEffect(() => {
      const handleStorageChange = () => {
        try {
          const storedCompanyData = localStorage.getItem("companyData");
          if (storedCompanyData) {
            const newCompanyData = JSON.parse(storedCompanyData);
            setCompanyData(newCompanyData);
            // Update browser tab title when company data changes
            if (newCompanyData?.name) {
              document.title = newCompanyData.name;
            }
          }
        } catch (error) {
          console.error("Error parsing updated company data:", error);
        }
      };

      window.addEventListener("storage", handleStorageChange);
      return () => {
        window.removeEventListener("storage", handleStorageChange);
      };
    }, []);

    const toggleDropdown = () => {
      if (!isDropdownOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          right: window.innerWidth - rect.right,
        });
      }
      setIsDropdownOpen(!isDropdownOpen);
    };

    const handleChangePassword = async (e) => {
      e.preventDefault();
      setChangePasswordMessage("");

      // Validate passwords match
      if (
        changePasswordData.newPassword !== changePasswordData.confirmPassword
      ) {
        setChangePasswordMessage("New passwords do not match");
        return;
      }

      // Validate password length
      if (changePasswordData.newPassword.length < 6) {
        setChangePasswordMessage(
          "New password must be at least 6 characters long"
        );
        return;
      }

      setChangePasswordLoading(true);

      try {
        const response = await authService.changePassword(
          changePasswordData.currentPassword,
          changePasswordData.newPassword
        );

        if (response.success) {
          setChangePasswordMessage("Password changed successfully!");
          setChangePasswordData({
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          });

          // Close modal after 2 seconds
          setTimeout(() => {
            setShowChangePassword(false);
            setChangePasswordMessage("");
          }, 2000);
        } else {
          setChangePasswordMessage(
            response.message || "Failed to change password"
          );
        }
      } catch (error) {
        setChangePasswordMessage(error.message || "An error occurred");
      } finally {
        setChangePasswordLoading(false);
      }
    };

    const resetChangePasswordForm = () => {
      setChangePasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setChangePasswordMessage("");
      setChangePasswordLoading(false);
    };

    return (
      <nav
        className={`shadow-sm z-40 sticky top-0 ${
          darkMode
            ? "bg-[#1A1918] text-[#C0BFBD]"
            : "bg-[#F5F5F5] text-[#1A1918]"
        }`}
      >
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-4">
          <div className="flex justify-between h-20">
            <div className="flex items-center space-x-4">
              {/* Company Logo or Power Icon */}
              <div className="flex-shrink-0">
                {companyData?.logo && isEmployeeView ? (
                  <img
                    src={companyData.logo}
                    alt={`${companyData.name} logo`}
                    style={{ width: "9.2rem" }}
                    onError={(e) => {
                      // If logo fails to load, hide the img and show SVG
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "block";
                    }}
                  />
                ) : null}
                <svg
                  className={`w-8 h-8 ${companyData?.logo ? "hidden" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>

              {/* Company Info */}
              <div className="flex flex-col justify-center">
                <div className="flex-shrink-0">
                  <h2
                    className={`text-xl font-bold truncate ${
                      darkMode ? "text-[#dbdadd]" : "text-[#1A1918]"
                    }`}
                  >
                    {companyData?.name || "HR Management System"}
                  </h2>
                </div>
                <div
                  className={`flex items-center text-sm ${
                    darkMode ? "text-[#C0BFBD]" : "text-[#1A1918]"
                  }`}
                >
                  <span className="font-medium">
                    <b>Home</b>
                  </span>
                  {currentPage && (
                    <>
                      <span className="mx-2">&gt;&gt;</span>
                      <span>{currentPage}</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center">
              <div className="flex items-center relative" ref={dropdownRef}>
                <button
                  ref={buttonRef}
                  onClick={toggleDropdown}
                  className="flex items-center focus:outline-none"
                  aria-label="User menu"
                  aria-haspopup="true"
                >
                  <span
                    className={`text-sm mr-2 hidden sm:inline ${
                      darkMode ? "text-[#C0BFBD]" : "text-[#1A1918]"
                    }`}
                  >
                    {user?.name}
                  </span>
                  <User
                    className={`h-8 w-8 rounded-full p-1 ${
                      darkMode
                        ? "bg-[#1F1F22] text-[#C8BCF6]"
                        : "bg-[#EFEFEF] text-[#1A1918]"
                    }`}
                  />
                  <ChevronDown
                    className={`ml-1 h-4 w-4 ${
                      darkMode ? "text-[#C0BFBD]" : "text-[#1A1918]"
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Change Password Modal */}
        {showChangePassword && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999]">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 text-center">
                  Change Password
                </h3>

                {changePasswordMessage && (
                  <div
                    className={`mb-4 p-3 rounded text-sm ${
                      changePasswordMessage.includes("successfully")
                        ? "bg-green-100 text-green-700 border border-green-400"
                        : "bg-red-100 text-red-700 border border-red-400"
                    }`}
                  >
                    {changePasswordMessage}
                  </div>
                )}

                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Current Password
                    </label>
                    <input
                      type="password"
                      required
                      value={changePasswordData.currentPassword}
                      onChange={(e) =>
                        setChangePasswordData({
                          ...changePasswordData,
                          currentPassword: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      New Password
                    </label>
                    <input
                      type="password"
                      required
                      value={changePasswordData.newPassword}
                      onChange={(e) =>
                        setChangePasswordData({
                          ...changePasswordData,
                          newPassword: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      required
                      value={changePasswordData.confirmPassword}
                      onChange={(e) =>
                        setChangePasswordData({
                          ...changePasswordData,
                          confirmPassword: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowChangePassword(false);
                        resetChangePasswordForm();
                      }}
                      className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={changePasswordLoading}
                      className={`flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                        changePasswordLoading
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      {changePasswordLoading
                        ? "Changing..."
                        : "Change Password"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Portal Dropdown */}
        {isDropdownOpen &&
          createPortal(
            <div
              data-portal-dropdown
              className={`fixed w-48 rounded-md shadow-lg py-1 z-[9999] ${
                darkMode ? "bg-[#1F1F22]" : "bg-white"
              } border ${darkMode ? "border-gray-700" : "border-gray-200"}`}
              style={{
                top: `${dropdownPosition.top}px`,
                right: `${dropdownPosition.right}px`,
              }}
            >
              <button
                onClick={() => {
                  setShowChangePassword(true);
                  setIsDropdownOpen(false);
                }}
                className={`flex items-center w-full px-4 py-2 text-sm ${
                  darkMode
                    ? "text-[#C0BFBD] hover:bg-[#1A1918] hover:text-[#C8BCF6]"
                    : "text-[#1A1918] hover:bg-[#F5F5F5] hover:text-[#C8BCF6]"
                }`}
              >
                <Lock className="mr-2 h-4 w-4" />
                Change Password
              </button>
              <button
                onClick={() => {
                  console.log(
                    "Logout button clicked, handleLogout:",
                    typeof handleLogout
                  );
                  if (typeof handleLogout === "function") {
                    handleLogout();
                    setIsDropdownOpen(false);
                  } else {
                    console.error(
                      "handleLogout is not a function:",
                      handleLogout
                    );
                    // Fallback: clear localStorage and reload page
                    localStorage.clear();
                    window.location.href = "/login";
                  }
                }}
                className={`flex items-center w-full px-4 py-2 text-sm ${
                  darkMode
                    ? "text-[#C0BFBD] hover:bg-[#1A1918] hover:text-[#C8BCF6]"
                    : "text-[#1A1918] hover:bg-[#F5F5F5] hover:text-[#C8BCF6]"
                }`}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </button>
            </div>,
            document.body
          )}
      </nav>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if these props actually change
    return (
      prevProps.currentPage === nextProps.currentPage &&
      prevProps.darkMode === nextProps.darkMode &&
      prevProps.isEmployeeView === nextProps.isEmployeeView
    );
  }
);

export default Navbar;
